import { useCallback, useState } from 'react';

import { useLocation } from 'react-router';

import { useNavigate } from '@change-corgi/core/react/router';

import { usePetitionDetailsPageData } from 'src/app/pages/petitionDetails/pageContext';
import { useCampaignTracking } from 'src/app/shared/hooks/campaignTracking';
import { useLoginState } from 'src/app/shared/hooks/session';

import type { MediaHitEmbed } from '../../../shared/context/types';
import { useMediaHitsPermissions } from '../../../shared/hooks/useMediaHitsPermissions';

type Result = ModelHookResult<
	{ canDeleteMediaHit: boolean; isModalOpen: boolean },
	{
		handleDeleteClick: () => void;
		handleModalResult: (result: { value: 'success' | 'error' | undefined }) => void;
		setIsModalOpen: (open: boolean) => void;
	}
>;

export function useDeleteMediaHit(media: MediaHitEmbed, removeMedia: (mediaId: string) => void): Result {
	const { track } = useCampaignTracking();
	const {
		data: {
			petition: { id: petitionId },
		},
	} = usePetitionDetailsPageData();
	const loginState = useLoginState();
	const location = useLocation();
	const navigate = useNavigate();
	const {
		data: { canDelete },
	} = useMediaHitsPermissions();

	const [isModalOpen, setIsModalOpen] = useState(false);

	const handleModalResult = useCallback(
		(result: { value: 'success' | 'error' | undefined }) => {
			if (result.value === 'success') {
				removeMedia(media.id);
			}
			setIsModalOpen(false);
		},
		[media.id, removeMedia],
	);

	const handleDeleteClick = useCallback(() => {
		void track('campaign_media_hit_delete_button_click', {
			petition_id: petitionId,
			media_id: media.id,
		});
		if (loginState === 'IDENTIFIED') {
			navigate(`/login_or_join?redirectTo=${location.pathname}`);
		} else {
			setIsModalOpen(true);
		}
	}, [location, loginState, navigate, petitionId, track, media.id]);

	return {
		data: {
			canDeleteMediaHit: canDelete,
			isModalOpen,
		},
		actions: {
			handleDeleteClick,
			handleModalResult,
			setIsModalOpen,
		},
	};
}
